'use client'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import Image, { StaticImageData } from 'next/image'
import Link from 'next/link'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Slider, { Settings } from 'react-slick'

import img1 from '@/assets/images/carousel-img-1.png'
import img2 from '@/assets/images/carousel-img-2.png'
import img3 from '@/assets/images/carousel-img-3.png'
import IslamicLogo from '@/assets/images/islamic-logo.png'
import { Button } from '@/blocks/components/ui/button'
import { PublicHeroSlideDto } from '@/server/public-api/types'
import { localizeHref } from '@/utils'
import { pickText } from '@/utils/text'

// Same-page hash targets ("#teachers" or "/#teachers") must render as a plain
// anchor so the browser scrolls on the current locale route; a next/Link to
// "/#teachers" would navigate to "/" and lose the hash. Everything else is a Link.
// Internal links are locale-prefixed (`localizeHref`): an unprefixed `/register`
// is resolved from the NEXT_LOCALE cookie / browser language, so an /ar visitor
// with an English browser would otherwise land on the English page.
const heroCtaChild = (href: string, label: string, locale: string) => {
  const hash = href.match(/^\/?(#[A-Za-z][\w-]*)$/)?.[1]
  return hash ? <a href={hash}>{label}</a> : <Link href={localizeHref(locale, href)}>{label}</Link>
}

// Hero photo card with a dark scrim laid over it (matching the rounded corners),
// so the images read consistently with the darkened heroes on the other apps.
const HeroCardImage = ({
  src,
  alt,
  width,
  height,
  priority,
  className,
  wrapperClassName = 'relative w-fit',
}: {
  src: StaticImageData | string
  alt: string
  width: number
  height: number
  priority?: boolean
  className: string
  wrapperClassName?: string
}) => (
  <div className={wrapperClassName}>
    <Image src={src} width={width} height={height} alt={alt} priority={priority} className={className} />
    <div className='pointer-events-none absolute inset-0 rounded-3xl bg-black/60' />
  </div>
)

const settings: Settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  draggable: true,
  arrows: false,
  autoplay: true,
  autoplaySpeed: 5000,
}

const HeroSection = ({ slides, academyName }: { slides?: PublicHeroSlideDto[]; academyName?: string | null }) => {
  const slick = useRef<Slider | null>(null)
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  // The static fallback names the academy from its own data; the legacy client
  // name (hero.title) is only used when that is missing.
  const name = pickText(academyName, t('hero.title'))

  // CMS hero (feature 022): published slides render in the same desktop/mobile
  // card layout as the static fallback below.
  if (slides && slides.length > 0) {
    return (
      <div id='hero' className='container scroll-mt-24'>
        <Slider {...settings} ref={slick}>
          {slides.map((slide, index) => (
            <div key={slide.id}>
              <section className='hidden justify-between gap-4 overflow-hidden pb-8 pt-32 md:flex'>
                <article className='w-3/5 space-y-32 text-center'>
                  {index === 0 ? (
                    <h1 className='text-white'>{slide.headline}</h1>
                  ) : (
                    <h2 className='text-white'>{slide.headline}</h2>
                  )}
                  {slide.supportingText ? (
                    <p className='text-2xl font-semibold text-white'>{slide.supportingText}</p>
                  ) : null}
                  {slide.ctaHref && slide.ctaLabel ? (
                    <Button
                      asChild
                      className='h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-[#009E9C] px-12 py-6 text-5xl text-tertiary-lightFonts'
                    >
                      {heroCtaChild(slide.ctaHref, slide.ctaLabel, locale)}
                    </Button>
                  ) : null}
                </article>
                <div className='w-2/5'>
                  <HeroCardImage
                    src={slide.image}
                    width={576}
                    height={599}
                    alt={slide.headline}
                    priority={index === 0}
                    className='h-[599px] w-[576px] rounded-3xl object-cover'
                  />
                </div>
              </section>

              <section className=' py-8 text-center md:hidden'>
                {index === 0 ? (
                  <h1 className='min-h-[100px] text-3xl text-white'>{slide.headline}</h1>
                ) : (
                  <h2 className='min-h-[100px] text-2xl text-white'>{slide.headline}</h2>
                )}
                {slide.supportingText ? (
                  <p className='mb-4 text-lg font-semibold text-white'>{slide.supportingText}</p>
                ) : null}
                <HeroCardImage
                  src={slide.image}
                  width={275}
                  height={300}
                  alt={slide.headline}
                  priority={index === 0}
                  wrapperClassName='relative mx-auto w-fit'
                  className='h-[300px] w-[275px] rounded-3xl object-cover'
                />
                {slide.ctaHref && slide.ctaLabel ? (
                  <Button
                    asChild
                    className='mt-4 h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-[#009E9C] px-8 py-4 text-2xl font-bold text-tertiary-lightFonts'
                  >
                    {heroCtaChild(slide.ctaHref, slide.ctaLabel, locale)}
                  </Button>
                ) : null}
              </section>
            </div>
          ))}
        </Slider>
        <figure>
          <Image src={IslamicLogo} width={207} height={184} alt='islamic-logo' className='max-sm:size-20' />
        </figure>
      </div>
    )
  }

  return (
    <div id='hero' className='container scroll-mt-24'>
      <Slider {...settings} ref={slick}>
        <div>
          <section className='hidden justify-between gap-4  overflow-hidden pb-8 pt-32 md:flex'>
            <article className='w-3/5 space-y-32 text-center'>
              <h1 className='text-white'>
                <span className='text-tertiary-lightFonts'>{t('hero.welcome_to')}</span> {name}
              </h1>
              {/* <Button
                asChild
                className='h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-primary-light px-12 py-6 text-5xl text-tertiary-lightFonts'
              >
                <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
              </Button> */}
            </article>
            <div className='w-2/5'>
              <HeroCardImage
                src={img1}
                width={576}
                height={599}
                alt={name}
                className='h-[599px] w-[576px] rounded-3xl object-cover'
              />
            </div>
          </section>

          <section className=' py-8 text-center md:hidden'>
            <h1 className='min-h-[100px] text-3xl text-white'>
              <span className='text-tertiary-lightFonts'>{t('hero.welcome_to')}</span> {name}
            </h1>
            <HeroCardImage
              src={img1}
              width={275}
              height={300}
              alt={name}
              wrapperClassName='relative mx-auto w-fit'
              className='h-[300px] w-[275px] rounded-3xl object-cover'
            />
            <Button
              asChild
              className='mt-4 h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-[#009E9C] px-8 py-4 text-2xl font-bold text-tertiary-lightFonts'
            >
              <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
            </Button>
          </section>
        </div>
        <div>
          <section className='hidden justify-between gap-4 overflow-hidden  pb-8 pt-32 md:flex'>
            <article className='w-3/5 space-y-32  text-center'>
              <h2 className='text-white'>{t('hero.experts_adults')}</h2>
              {/* <Button
                asChild
                className='h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-primary-light px-12 py-6 text-5xl text-tertiary-lightFonts'
              >
                <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
              </Button> */}
            </article>
            <div className='w-2/5'>
              <HeroCardImage
                src={img2}
                width={576}
                height={599}
                alt={name}
                className='h-[599px] w-[576px] rounded-3xl object-cover'
              />
            </div>
          </section>

          <section className=' py-8 text-center md:hidden'>
            <h2 className='min-h-[100px] text-2xl text-white'>{t('hero.experts_adults')} </h2>
            <HeroCardImage
              src={img2}
              width={275}
              height={300}
              alt={name}
              wrapperClassName='relative mx-auto w-fit'
              className='h-[300px] w-[275px] rounded-3xl object-cover'
            />
            <Button
              asChild
              className='mt-4 h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-[#009E9C] px-8 py-4 text-2xl font-bold text-tertiary-lightFonts'
            >
              <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
            </Button>
          </section>
        </div>
        <div>
          <section className='hidden justify-between gap-4 overflow-hidden  pb-8 pt-32 md:flex'>
            <article className='w-3/5 space-y-32  text-center'>
              <h2 className='text-white'>{t('hero.skilled_children')}</h2>
              {/* <Button
                asChild
                className='h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-primary-light px-12 py-6 text-5xl text-tertiary-lightFonts'
              >
                <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
              </Button> */}
            </article>
            <div className='w-2/5'>
              <HeroCardImage
                src={img3}
                width={576}
                height={599}
                alt={name}
                className='h-[599px] w-[576px] rounded-3xl object-cover'
              />
            </div>
          </section>
          <section className=' py-8 text-center md:hidden'>
            <h2 className='min-h-[100px] text-2xl text-white'>{t('hero.skilled_children')} </h2>
            <HeroCardImage
              src={img3}
              width={275}
              height={300}
              alt={name}
              wrapperClassName='relative mx-auto w-fit'
              className='h-[300px] w-[275px] rounded-3xl object-cover'
            />
            <Button
              asChild
              className='mt-4 h-auto rounded-3xl border-4 border-tertiary-lightFonts bg-[#009E9C] px-8 py-4 text-2xl font-bold text-tertiary-lightFonts'
            >
              <Link href={localizeHref(locale, '/register')}>{t('nav.free_trial')}</Link>
            </Button>
          </section>
        </div>
      </Slider>
      <figure>
        <Image src={IslamicLogo} width={207} height={184} alt='islamic-logo' className='max-sm:size-20' />
      </figure>
    </div>
  )
}

export default HeroSection
